import { TextChatMessage } from '../types/text-chat';

/**
 * The chat history carried over a device switch, shared with the mobile app. Encrypted
 * before upload (see chat-crypto.util), so only the two devices ever read this format.
 */
export const CHAT_HISTORY_VERSION = 1;
export const MAX_HISTORY_MESSAGES = 500;
export const MAX_HISTORY_BYTES = 256 * 1024;

export interface ChatHistoryEntry {
    id: string;
    from: string;
    text: string;
    ts: number;
}

interface ChatHistoryPayload {
    v: number;
    messages: ChatHistoryEntry[];
}

const encoder = new TextEncoder();

function byteLength(value: string): number {
    return encoder.encode(value).length;
}

function toEntry(message: TextChatMessage): ChatHistoryEntry {
    return {
        id: String(message.id),
        from: message.from ?? '',
        text: message.text,
        ts: message.ts ?? 0,
    };
}

/**
 * Keeps the newest messages that fit both limits. Returns null when there is nothing to send.
 */
export function buildChatHistory(
    messages: TextChatMessage[],
): { serialized: string; count: number } | null {
    const entries = messages.slice(-MAX_HISTORY_MESSAGES).map(toEntry);
    const kept: ChatHistoryEntry[] = [];
    let size = byteLength(JSON.stringify({ v: CHAT_HISTORY_VERSION, messages: [] }));

    for (let i = entries.length - 1; i >= 0; i--) {
        const entrySize = byteLength(JSON.stringify(entries[i])) + 1;
        if (size + entrySize > MAX_HISTORY_BYTES) {
            break;
        }
        size += entrySize;
        kept.unshift(entries[i]);
    }

    if (!kept.length) {
        return null;
    }

    const payload: ChatHistoryPayload = { v: CHAT_HISTORY_VERSION, messages: kept };

    return { serialized: JSON.stringify(payload), count: kept.length };
}

function isEntry(value: any): value is ChatHistoryEntry {
    return (
        value &&
        typeof value.id === 'string' &&
        typeof value.from === 'string' &&
        typeof value.text === 'string' &&
        typeof value.ts === 'number'
    );
}

/** Throws on an unknown version or a malformed payload. */
export function parseChatHistory(serialized: string): ChatHistoryEntry[] {
    const payload = JSON.parse(serialized) as ChatHistoryPayload;
    if (payload?.v !== CHAT_HISTORY_VERSION || !Array.isArray(payload.messages)) {
        throw new Error(`Unsupported chat history version: ${payload?.v}`);
    }

    return payload.messages.filter(isEntry);
}

/** The user's identity is the same on every device, so it tells their own messages apart. */
export function toTextChatMessages(
    entries: ChatHistoryEntry[],
    localIdentity: string,
): TextChatMessage[] {
    return entries.map((entry) => ({
        id: entry.id,
        text: entry.text,
        type: entry.from && entry.from === localIdentity ? 'outgoing' : 'incoming',
        isRead: true,
        from: entry.from,
        ts: entry.ts,
    }));
}
